import { toError } from 'src/app/common/logging/error-normalization';

import { SessionState } from './session-state-types';

/**
 * The one logging interface of the session (#26615, #27294). Three calls, nothing else:
 *
 * - `logStep()` / `logTransition()` — one console line each. Sentry's console integration (on by
 *   default, see `app.component.ts`) turns it into a breadcrumb, so there is nothing to do twice.
 *   The last lines also travel with every Sentry event as the `session` context (see
 *   `HISTORY_LINES`), so the history survives the breadcrumb churn of a sync run.
 * - `logSessionEvent()` — one Sentry event for the few outcomes worth counting or alerting on.
 *
 * Callers pass a code and a cause, never free text alone: both become tags and the fingerprint,
 * so one root cause is one Sentry issue and a dashboard can filter by it. The free text is kept
 * as an extra for reading. Everything here is diagnostics: it never throws, never awaits and
 * never keeps the app from doing its work.
 */
export type SessionEventCode =
  /** warning: the session ended although the user did not ask for it; the cause says why */
  | 'forced_logout'
  /** error: something that should never happen (account lost while the token is there, evaluation threw, …) */
  | 'session_anomaly'
  /** warning: one cleanup step of a logout failed; the logout itself still finished */
  | 'logout_step_failed'
  /** warning: a sign-in the user started did not end in a token; the cause is the identity provider's code */
  | 'sign_in_failed'
  /** info: a token of the previous OAuth library was taken over on the first start after the update */
  | 'token_migration'
  /** warning: the migrated token could not be saved; it is used for this process and migrated again next start */
  | 'token_migration_failed'
  /** warning: the backend has been unusable (`degraded`) for a long time */
  | 'degraded_long'
  /** error: the session did not leave `starting`, so the user sees nothing but the splash screen */
  | 'stuck_starting'
  /** warning: a data call got a 401 while the session was not `online` — a call that ran too early */
  | 'unauthenticated_call';

type SessionEventLevel = 'error' | 'warning' | 'info';

const LEVELS: Record<SessionEventCode, SessionEventLevel> = {
  forced_logout: 'warning',
  session_anomaly: 'error',
  logout_step_failed: 'warning',
  sign_in_failed: 'warning',
  token_migration: 'info',
  token_migration_failed: 'warning',
  degraded_long: 'warning',
  stuck_starting: 'error',
  unauthenticated_call: 'warning',
};

/**
 * Sent at most once per process and cause: these describe a condition, not an occurrence, and
 * would otherwise repeat with every retry or every data call.
 */
const ONCE_PER_PROCESS = new Set<SessionEventCode>(['degraded_long', 'stuck_starting', 'unauthenticated_call']);

/** The session history attached to every Sentry event: small on purpose, an event must never grow past the request limit. */
const HISTORY_LINES = 30;
const HISTORY_LINE_LENGTH = 160;

const history: string[] = [];
const sentOnce = new Set<string>();

export function logTransition(from: SessionState, to: SessionState, reason: string): void {
  write(`[session] ${from} -> ${to} | ${reason}`);
}

export function logStep(step: string, detail: string): void {
  write(`[session] ${step} | ${detail}`);
}

/**
 * One Sentry event. `code` and `cause` are the low-cardinality parts (tags, fingerprint), `detail`
 * is the free text for reading, `error` the exception when there is one.
 */
export function logSessionEvent(code: SessionEventCode, cause: string, detail: string, error?: unknown): void {
  write(`[session] ${code} (${cause}) | ${detail}`);
  const key = `${code}:${cause}`;
  if (ONCE_PER_PROCESS.has(code) && sentOnce.has(key)) {
    return;
  }
  sentOnce.add(key);
  try {
    window.logger?.captureErrorWithExtras(
      `[session] ${code}: ${cause}`,
      error === undefined ? null : toError(error, detail),
      new Map([
        ['session.event', code],
        ['session.cause', cause],
      ]),
      LEVELS[code],
      { detail },
      ['session', code, cause]
    );
  } catch {
    // diagnostics only
  }
}

/** The last session lines, oldest first. Exposed for the telemetry and for specs. */
export function sessionHistory(): readonly string[] {
  return history;
}

/** Formats a remaining lifetime for log lines, e.g. `11:58`. */
export function formatRemaining(expiresAtMs: number, now = Date.now()): string {
  const totalSeconds = Math.max(0, Math.round((expiresAtMs - now) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function describeError(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  const candidate = error as { message?: unknown; code?: unknown; className?: unknown };
  const parts = [candidate?.className, candidate?.code, candidate?.message].filter(
    (part): part is string | number => (typeof part === 'string' && part.length > 0) || typeof part === 'number'
  );
  return parts.length > 0 ? parts.join(' ') : String(error);
}

function write(message: string): void {
  console.info(message);
  history.push(message.length > HISTORY_LINE_LENGTH ? `${message.slice(0, HISTORY_LINE_LENGTH - 1)}…` : message);
  if (history.length > HISTORY_LINES) {
    history.splice(0, history.length - HISTORY_LINES);
  }
  try {
    window.logger?.setContext?.('session', { history: [...history] });
  } catch {
    // diagnostics only — the logger is not available before AppInitService ran
  }
}
